import React, { Fragment, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { bool, string, array, oneOfType, number, object } from 'prop-types';
import { map, get, isEmpty, head, filter, inRange, toNumber, size } from 'lodash';
import moment from 'moment';
import { Text, FlexContainer, Row, Divider, Column, IconContainer, Icon, Button, HasPermissions, Truncate, AppIcon } from '../../common';
import { Switch, Modal, Drawer, Tooltip } from 'antd';
import { withApollo } from 'react-apollo';
import SWITCH_WINDOW from '../../graphql/automaticAssignment';
import { calculateBillingDeadline, getFilterSlaType } from '../../../utils/misc';

const VerticalSeparator = styled.div`
    border-left: 1px solid #eaeaea;
    width: 1px;
    display: inline-block;
    margin-left: 15px;
    margin-right: 15px;
`;

const CounterButton = styled.div`
    text-align: center;
    background-color: #f4f4f4;
    border-radius: 11.5px;

    ${({ hover }) => hover && css`&:hover {
        background-color: ${({ backgroundColorOnHover = '#969696' }) => backgroundColorOnHover};
        span {
            color: #fff;
        }
        cursor: pointer;
    }`};
`;

const StatusField = ({ label, value, salesChannel, isTotal, backgroundColorOnHover, dateRange, selectedSalesChannels, color, filterSelectedSla, nameCarrier }) => {
    const leftColumWidth = '44px';

    const getStatusFilter = () => {
        switch (label) {
        case 'Pendientes':
            return '&status=1-2-7-20';
        case 'En picking':
            return '&status=3-6-30-32-4-8-9';
        case 'Pickeados':
            return '&status=5-50-51-53-54-55-56-57-58-59-60';
        case 'Anulados':
            return '&status=40-41-34';
        default:
            return '';
        };
    };

    return (
        <Row padding='3px 0px' align='center'>
            <div style={{ width: `calc(100% - ${leftColumWidth})` }}>
                <Text text={label} strong={isTotal || (label === 'Pendientes')} color={color} />
            </div>
            {toNumber(value) > 0
                ? <Link target='_blank'
                    to={{
                        pathname: '/orders',
                        search: `nameCarrier=${nameCarrier}&selectedSla=${filterSelectedSla || ''}${getStatusFilter()}&salesChannel=${(salesChannel || selectedSalesChannels) || ''}&range=${dateRange || ''}&fromRoomManagement=true`
                    }}>
                    <CounterButton
                        hover
                        style={{ width: leftColumWidth }}
                        backgroundColorOnHover={backgroundColorOnHover}
                    >
                        {isTotal
                            ? <Text text={value} strong />
                            : <Text text={value} style={{ width: leftColumWidth, textAlign: 'center' }} color={color} strong={label === 'Pendientes'} />
                        }
                    </CounterButton>
                </Link>
                : <CounterButton style={{ width: leftColumWidth }}>
                    <Text text={value} style={{ width: leftColumWidth, textAlign: 'center' }} color={color} strong={label === 'Pendientes'} />
                </CounterButton>
            }
        </Row>
    );
};

StatusField.propTypes = {
    label: string,
    value: string,
    isTotal: bool,
    backgroundColorOnHover: string,
    filterSelectedSla: array,
    salesChannel: oneOfType([string, () => undefined]),
    dateRange: array,
    selectedSalesChannels: array,
    color: string,
    nameCarrier: string
};

StatusField.defaultProps = {
    label: '',
    value: 0,
    isTotal: false
};

const SlaCard = ({
    selectedSalesChannels,
    assignmentKind,
    client,
    maxHrsLimitWindow,
    card,
    isMobile,
    hoursAssignDispatch,
    userId,
    windowLogs,
    isDeliveryTomorrow,
    isEarlyDelivery
}) => {
    const slaData = get(card, 'data');
    const filterSelectedSla = getFilterSlaType(get(card, 'info.selectedSla'));
    const selectedSla = get(card, 'info.selectedSla');
    const salesChannel = get(card, 'info.salesChannel');
    const dateRange = [get(card, 'info.init'), get(card, 'info.end')];
    const windowInit = get(card, 'info.init');
    const windowEnd = get(card, 'info.end');
    const pending = get(head(filter(slaData, { status: 'pendiente' })), 'count');
    const cancelled = get(head(filter(slaData, { status: 'anulado' })), 'count');
    const inPicking = get(head(filter(slaData, { status: 'inPicking' })), 'count');
    const picked = get(head(filter(slaData, { status: 'facturado' })), 'count');
    const statusAutomaticAssign = get(card, 'info.automaticAssign');
    const isDispatch = selectedSla === 'Despacho';
    const totalOrders = pending + cancelled + inPicking + picked;
    const allPicked = (totalOrders - cancelled) === picked;
    const cutOffTime = get(card, 'info.cutOffTime');
    const maximunCapacity = get(card, 'info.maximunCapacity');
    const consumedCapacity = get(card, 'info.consumedCapacity');
    const category = get(card, 'info.category');
    const time = get(card, 'info.time');
    const nameCarrier = get(card, 'info.nameCarrier');

    const getDate = () => (
        calculateBillingDeadline(category, time, windowInit, windowEnd).format('YYYY-MM-DD HH:mm:ss')
    );

    const [autoAssigment, setAutoAssigment] = useState(statusAutomaticAssign);
    const [visibleModal, setVisibleModal] = useState(false);
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [loading, setLoading] = useState(false);

    const items = [
        { label: 'Pendientes', count: `${pending}` },
        { label: 'En picking', count: `${inPicking}` },
        { label: 'Pickeados', count: `${picked}` },
        { label: 'Anulados', count: `${cancelled}` }
    ];
    const totals = [
        { label: 'Total', count: `${pending + inPicking + picked + cancelled}` }
    ];

    const dateNow = moment().format('YYYY-MM-DD HH:mm:ss');
    const timeLeftTemp = moment(getDate()).diff(moment(dateNow), 'seconds');
    const duration = moment.duration(moment(getDate()).diff(moment(dateNow)));
    const timeLimitTemp = inRange(timeLeftTemp, 60, 1860);
    let timeOutTemp = true;

    if (!(get(head(filter(slaData, { status: 'fueraDePlazo' })), 'count') > 0)) {
        timeOutTemp = dateNow >= getDate();
    };

    const getColorBrandCard = () => {
        if (timeOutTemp && !allPicked) return '#CC444B';
        if (timeLimitTemp) return '#EB963C';
        return '#e3e3e3';
    };

    const getColorTextTemp = () => {
        if ((timeOutTemp && !allPicked) || timeLimitTemp) return '#FFFFFF';
        return '#000000';
    };

    const isOverTimeLimit = (maxHrs) => {
        const hoursLeft = moment(windowInit).diff(moment(), 'hours');
        return hoursLeft > maxHrs;
    };

    const switchWindow = async () => {
        setLoading(true);
        const { data } = await client.mutate({
            mutation: SWITCH_WINDOW,
            variables: {
                input: {
                    turnOn: !autoAssigment ? 'true' : 'false',
                    salesChannel: toNumber(salesChannel),
                    selectedSla,
                    windowStart: windowInit,
                    windowEnd: windowEnd,
                    courierName: nameCarrier,
                    userId
                }
            }
        });
        const { switchWindow: { internalCode } } = data;
        internalCode === '200' && setAutoAssigment(!autoAssigment);
        onClose();
        setLoading(false);
    };

    const onClose = () => {
        setVisibleModal(false);
    };

    const ModalFooter = () => {
        return (
            <FlexContainer justify='flex-end' align='center' style={{ width: '100%' }}>
                <Button
                    link
                    text={'btn.action.cancel'}
                    style={{ width: '100px', margin: 0 }}
                    onClick={() => onClose()}
                />
                <Button
                    loading={loading}
                    text={'btn.action.confirm'}
                    style={{ width: '100px', margin: 0 }}
                    onClick={() => switchWindow()()}
                />
            </FlexContainer>
        );
    };

    const modalWarning = () => {
        return (
            <Modal
                visible={visibleModal}
                footer={<ModalFooter />}
                closable={false}
                width={400}
                destroyOnClose
                onCancel={() => onClose()}
            >
                <Column center>
                    <IconContainer color='#f38630' margin='0 0 40px 0'>
                        <Icon name='caution' size={43} color='#ffffff' />
                    </IconContainer>
                    <Text text={`La hora de entrega esta muy lejana`} size={14} centered />
                    <Text text={`Desea confirmar?`} size={14} centered />
                </Column>
            </Modal>
        );
    };

    useEffect(() => {
        setTimeout(() => {
            setTemporalizator(handleTemporalizator());
        }, 1000);
    });
    
    const formatTime = (time) =>
        time < 10 ? `0${time}` : time;

    const handleTemporalizator = () => {
        let temp = {};
        if (timeLeftTemp > 0) {
            temp = {
                hours: formatTime(duration.days() >= 1 ? duration.hours() + (24 * duration.days()) : duration.hours()),
                minutes: formatTime(duration.minutes()),
                seconds: formatTime(duration.seconds())
            };
        }
        return temp;
    };

    const [temporalizator, setTemporalizator] = useState(handleTemporalizator());
    const tempText = `${temporalizator.hours}:${temporalizator.minutes}:${temporalizator.seconds}`;

    const getTempText = () => {
        return !isEmpty(temporalizator) ? tempText : '00:00:00';
    };

    const activeRestriction = () => {
        return isDispatch && moment(windowInit).diff(moment(), 'minutes') >= hoursAssignDispatch;
    };

    const renderMaximumQuantity = () => {
        const section = (label, value) => (<Row justify='center' style={{ gap: 5 }}>
            <Text text={value || '0'} strong />
            <Text text={label} />
        </Row>);

        return (<>
            <Divider style={{ marginTop: 5 }} />
            <Row justify='center'>
                <Text text={'Cantidad máxima'} strong />
            </Row>
            <Row mT={10}>
                {section('Ingresadas', consumedCapacity)}
                <VerticalSeparator />
                {section('Capacidad', maximunCapacity)}
            </Row>
        </>);
    };

    const logsFound = filter(windowLogs, ({ windowStart: init, windowEnd: end, courierName, selectedSla: typeDelivery }) => {
        return windowInit === init && windowEnd === end && nameCarrier === courierName && selectedSla === typeDelivery;
    })

    const toggleDrawer = () => setDrawerOpened(!drawerOpened);

    return (
        <FlexContainer
            direction='column'
            style={{
                flex: '1 0 250px',
                backgroundColor: 'white',
                minWidth: isMobile ? '85%' : '18%',
                maxWidth: 360,
                borderRadius: 4,
                boxShadow: 'rgb(0 0 0 / 20%) 3px 0px 0px -4px, rgb(0 0 0 / 6%) 0px 1px 0px 0px, rgb(0 0 0 / 6%) 1px 0px 0px 0px'
            }}
        >
            <Row style={{ backgroundColor: `${getColorBrandCard()}`, borderTopLeftRadius: '4px', borderTopRightRadius: '4px', padding: '0px 15px' }} justify='space-between'>
                <Text text={'Límite de Facturación'} color={getColorTextTemp()} />
                <Text text={getTempText()} color={getColorTextTemp()} />
            </Row>
            <div style={{ padding: '15px' }}>
                {!isEmpty(nameCarrier) && <>
                    <Row padding='0px 0px 3px 10px' justify='center' style={{ position: 'relative' }} gap={10} >
                        {isEarlyDelivery && <Tooltip title='Ventana anticipada'>
                            <AppIcon name='ic-location' size={22} />
                        </Tooltip>}
                        <Truncate width={200} style={{ textAlign: 'left' }}>
                            <Text text={nameCarrier} strong />
                        </Truncate>
                        <HasPermissions to={['room-management-page:automatic-assignment-logs']} >
                            {(size(logsFound) > 0) && <Tooltip placement='top' title='Logs de ventana'>
                                <div style={{ position: 'absolute', right: 0 }}>
                                    <Icon
                                        name='logs'
                                        color='#1fa02e'
                                        size={22}
                                        onClick={toggleDrawer}
                                        pointer
                                    />
                                </div>
                            </Tooltip>}
                        </HasPermissions>
                    </Row>
                    <Divider style={{ marginTop: 5 }} />
                </>}

                {/* Card title */}
                <Row>
                    <Link target='_blank'
                        to={{
                            pathname: '/orders',
                            search: `nameCarrier=${nameCarrier}&selectedSla=${filterSelectedSla || ''}&salesChannel=${(salesChannel || selectedSalesChannels) || ''}&range=${dateRange || ''}&fromRoomManagement=true`
                        }}
                    >
                        <Text text={selectedSla} cursor='pointer' underlineOnHover strong />
                    </Link>
                    {assignmentKind && <Row justify='flex-end'>
                        <HasPermissions to={['room-management-page:automatic-assignment']} >
                            <Text text='Asig. Auto' size={8} color={'b8bcbf'} style={{ margin: 'auto 5px' }} />
                            <Switch
                                disabled={activeRestriction()}
                                loading={loading}
                                checked={autoAssigment}
                                onChange={() => {
                                    isOverTimeLimit(maxHrsLimitWindow)
                                        ? setVisibleModal(true)
                                        : switchWindow();
                                }}
                            />
                        </HasPermissions>
                    </Row>
                    }
                </Row>
                <Column gap={3}>
                    <Text text={moment(windowInit).format('DD/MM/YYYY')} />
                    <Text text='Ventana Entrega' />
                    <Row gap={5} align='center'>
                        <Text text={`${moment(windowInit).format('HH:mm')} - ${moment(windowEnd).format('HH:mm')}`} strong />
                        {isDeliveryTomorrow && isEarlyDelivery && <Tooltip title='Ventana anticipada (mañana)'>
                            <AppIcon name='ic-tomorrow' size={22} />
                        </Tooltip>}
                    </Row>
                    <Divider style={{ marginTop: 5, marginBottom: 0 }} />
                </Column>
                <Row padding='3px 0px' align='center'>
                    <div style={{ width: `calc(100% - 44px)` }}>
                        <Text text={'Cierre de oferta'} strong />
                    </div>
                    <Text text={cutOffTime} style={{ width: 44, textAlign: 'center' }} strong />
                </Row>
                <Divider style={{ marginTop: 5 }} />
                {/* Card body */}
                { map(items, ({ label, count }, index) => (
                    <Fragment key={index}>
                        <StatusField
                            nameCarrier={nameCarrier}
                            label={label}
                            value={count}
                            selectedSla={selectedSla}
                            filterSelectedSla={filterSelectedSla}
                            salesChannel={salesChannel}
                            dateRange={dateRange}
                            selectedSalesChannels={selectedSalesChannels}
                        />
                    </Fragment>
                )) }
                <Divider style={{ marginTop: 5 }} />
                {/* Card totals */}
                { map(totals, ({ label, count }, index) => <StatusField
                    nameCarrier={nameCarrier}
                    key={index}
                    label={label}
                    isTotal
                    value={count}
                    selectedSla={selectedSla}
                    filterSelectedSla={filterSelectedSla}
                    salesChannel={salesChannel}
                    dateRange={dateRange}
                    selectedSalesChannels={selectedSalesChannels}
                />) }
                {modalWarning()}
                {renderMaximumQuantity()}
            </div>
            <Drawer
                title={<Text text={<div>
                    <strong>Logs de ventana </strong> ({moment(windowInit).format('HH:mm')} - {moment(windowEnd).format('HH:mm')})
                </div>} size={15} />}
                closable
                visible={drawerOpened}
                onClose={toggleDrawer}
                width='50%'
            >
                {map(logsFound, ({ userId, switchType, creationAt }) => {
                    return (
                        <Row padding='3px 0px' align='center'>
                            <Text text={switchType === "true" ? 'Apertura' : 'Cierre'} size={12} style={{ width: 60 }} />
                            <Text text={creationAt} size={12} style={{ width: 130 }} />
                            <Text text={userId} strong size={12} />
                        </Row>
                    )
                })}
            </Drawer>
        </FlexContainer>
    );
};

SlaCard.propTypes = {
    selectedSalesChannels: array,
    assignmentKind: bool,
    client: object,
    maxHrsLimitWindow: number,
    card: object,
    isMobile: bool,
    hoursAssignDispatch: number,
    userId: string,
    isDeliveryTomorrow: bool,
    isEarlyDelivery: bool
};

export default withApollo(SlaCard);